"use client"

import Link from "next/link"
import {
    ColumnDef,
    ColumnFiltersState,
    SortingState,
    VisibilityState,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from "@tanstack/react-table"
import { Checkbox } from "../../../shared/components/ui/checkbox"
import {
    Table,
    TableHead,
    TableHeader,
    TableRow as DefaultTableRow,
} from "../../../shared/components/ui/table"
import React, { PropsWithChildren } from "react"
import { buildRegionUrl, cn, formatPhoneNumber, shortenName } from "../../../shared/lib/utils"
import { Avatar, AvatarFallback, AvatarImage } from "../../../shared/components/ui/avatar"
import { Badge } from "../../../shared/components/ui/badge"
import { Volunteer, NonHomeownerUser, TableDataItem } from "../types/volunteers.type"
import VolunteerTableBody from "./volunteer-table-body"
import { format } from "date-fns"
import DeleteVolunteerButton from "./delete-volunteer-button"
import EditVolunteerButton from "./edit-volunteer-button"
import DeleteUserButton from "./delete-user-button"
import EditUserButton from "./edit-user-button"
import { UpdateUserLink } from "./edit-user-button"
import { useSession } from "@/features/auth/context/session.context"
import { UserRole } from "@/shared/constants/user-roles"
import { Button } from "../../../shared/components/ui/button"
import { Filter } from "lucide-react"
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "../../../shared/components/ui/popover"
import { Input } from "../../../shared/components/ui/input"
import { Calendar } from "../../../shared/components/ui/calendar"
import { roleFilterOptions, statusFilterWithAllOptions } from "../constants/options"
import { useRegion } from "@/shared/hooks/use-region"


// Helper function to parse a date value (handles YYYY-MM-DD and ISO strings)
const parseDateValue = (dateValue: string | Date | undefined | null): Date | null => {
    if (!dateValue) return null
    
    if (dateValue instanceof Date) {
        return isNaN(dateValue.getTime()) ? null : dateValue
    }
    
    if (typeof dateValue === 'string') {
        // If it's a date-only string (YYYY-MM-DD), parse as local time
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
            const [year, month, day] = dateValue.split('-').map(Number)
            const date = new Date(year, month - 1, day)
            return isNaN(date.getTime()) ? null : date
        }
        // ISO string with time, parse normally
        const date = new Date(dateValue)
        return isNaN(date.getTime()) ? null : date
    }
    
    return null
}

// Helper function to find the appropriate serve period based on current date
// Priority: 1. Period containing today, 2. Next upcoming period, 3. Most recent past period
const findRelevantServePeriod = (serveDates: Array<{ from: string | Date; to: string | Date }> | null | undefined): { from: Date | null; to: Date | null } => {
    if (!serveDates || !Array.isArray(serveDates) || serveDates.length === 0) {
        return { from: null, to: null }
    }
    
    const today = new Date()
    today.setHours(0, 0, 0, 0) // Normalize to midnight for comparison
    
    // Parse all periods
    const parsedPeriods = serveDates
        .map(period => {
            const from = parseDateValue(period.from)
            const to = parseDateValue(period.to)
            if (!from || !to) return null
            
            // Normalize dates to midnight for comparison
            const fromNormalized = new Date(from.getFullYear(), from.getMonth(), from.getDate())
            const toNormalized = new Date(to.getFullYear(), to.getMonth(), to.getDate())
            
            return { from: fromNormalized, to: toNormalized, originalFrom: from, originalTo: to }
        })
        .filter((period): period is NonNullable<typeof period> => period !== null)
        .sort((a, b) => a.from.getTime() - b.from.getTime()) // Sort by start date
    
    if (parsedPeriods.length === 0) {
        return { from: null, to: null }
    }
    
    // 1. Find period containing today
    const currentPeriod = parsedPeriods.find(period => 
        today >= period.from && today <= period.to
    )
    if (currentPeriod) {
        return { from: currentPeriod.originalFrom, to: currentPeriod.originalTo }
    }
    
    // 2. Find next upcoming period (starts after today)
    const upcomingPeriod = parsedPeriods.find(period => period.from > today)
    if (upcomingPeriod) {
        return { from: upcomingPeriod.originalFrom, to: upcomingPeriod.originalTo }
    }
    
    // 3. All periods are in the past, return the most recent one (last in sorted array)
    const mostRecentPeriod = parsedPeriods[parsedPeriods.length - 1]
    return { from: mostRecentPeriod.originalFrom, to: mostRecentPeriod.originalTo }
}

// Helper functions to extract data from both types
// organizationName: volunteers table only (no users table fallback)
type NormalizedUserData = NonHomeownerUser & {
    organizationName?: string | null
}

const getUserData = (item: TableDataItem): NormalizedUserData => {
    if ('user' in item) {
        const user = item.user
        const org = (item as Volunteer).organizationName
        return { ...user, organizationName: org ?? undefined } as NormalizedUserData
    }
    return item as NormalizedUserData
}

type VolunteerData = {
    status: boolean
    locationServing: string | null
    arrivalDate: string | null
    departureDate: string | null
    serveDates: Array<{ from: string | Date; to: string | Date }> | null
}

const getVolunteerData = (item: TableDataItem): VolunteerData => {
    if ('user' in item) {
        const raw = item.serveDates ?? null
        const serveDates: Array<{ from: string | Date; to: string | Date }> | null =
            Array.isArray(raw) && raw.length > 0 ? raw : null
        return {
            status: item.status,
            locationServing: item.locationServing ?? null,
            arrivalDate: item.arrivalDate ?? null,
            departureDate: item.departureDate ?? null,
            serveDates,
        }
    } else {
        const v = item.volunteer as { status?: boolean; locationServing?: string | null; arrivalDate?: string | null; departureDate?: string | null; serveDates?: Array<{ from: string; to: string }> | null; regionId?: number | null; region?: unknown } | undefined
        const rawServeDates = v?.serveDates
        const serveDates: Array<{ from: string | Date; to: string | Date }> | null =
            Array.isArray(rawServeDates) && rawServeDates.length > 0 ? rawServeDates : null
        return {
            status: v?.status ?? true,
            locationServing: v?.locationServing ?? null,
            arrivalDate: v?.arrivalDate ?? null,
            departureDate: v?.departureDate ?? null,
            serveDates,
        }
    }
}

// Combo box filter: type to search + select from list (like Homeowner Name filter)
const SUGGESTIONS_MAX = 80

function ComboBoxFilterContent({
    value,
    onApply,
    suggestions = [],
    placeholder = "Type to search…",
}: {
    value: string
    onApply: (v: string | null) => void
    suggestions: string[]
    placeholder?: string
}) {
    const [local, setLocal] = React.useState(value)
    const [isListOpen, setIsListOpen] = React.useState(false)

    React.useEffect(() => {
        setLocal(value)
    }, [value])

    const filtered = React.useMemo(() => {
        if (!suggestions.length) return []
        const q = local.trim().toLowerCase()
        const filteredList = q
            ? suggestions.filter((s) => s.toLowerCase().includes(q))
            : suggestions
        return filteredList.slice(0, SUGGESTIONS_MAX)
    }, [suggestions, local])

    const showList = isListOpen && filtered.length > 0

    const handleSelect = (item: string) => {
        setLocal(item)
        onApply(item)
        setIsListOpen(false)
    }

    const hasActive = !!value

    return (
        <div className="w-64 p-2 space-y-2">
            <Input
                value={local}
                onChange={(e) => {
                    setLocal((e.target as HTMLInputElement).value)
                    setIsListOpen(true)
                }}
                onFocus={() => setIsListOpen(true)}
                onBlur={() => setTimeout(() => setIsListOpen(false), 150)}
                onKeyDown={(e) => {
                    if (e.key === "Enter") {
                        e.preventDefault()
                        onApply(local.trim() || null)
                    }
                }}
                placeholder={placeholder}
                className="h-8"
            />
            {showList && (
                <div className="border rounded-md bg-muted/50 max-h-40 overflow-y-auto">
                    {filtered.map((s) => (
                        <button
                            key={s}
                            type="button"
                            className="w-full px-2 py-1.5 text-left text-sm hover:bg-accent focus:bg-accent focus:outline-none"
                            onMouseDown={(e) => {
                                e.preventDefault()
                                handleSelect(s)
                            }}
                        >
                            {s}
                        </button>
                    ))}
                </div>
            )}
            <div className="flex gap-2">
                <Button size="sm" variant="default" onClick={() => onApply(local.trim() || null)}>
                    Apply
                </Button>
                {hasActive && (
                    <Button size="sm" variant="ghost" className="text-muted-foreground" onClick={() => onApply(null)}>
                        Clear
                    </Button>
                )}
            </div>
        </div>
    )
}

// Column filter content components (same pattern as Homeowners)
function RolesFilterContent({
    value,
    onApply,
    options,
}: {
    value: string[]
    onApply: (v: string[] | null) => void
    options: readonly { value: string; label: string }[]
}) {
    const selected = React.useMemo(
        () => (value.length ? value : (typeof value === "string" && value ? [value] : [])),
        [value]
    )
    const [local, setLocal] = React.useState<string[]>(Array.isArray(selected) ? selected.slice() : [])
    const selectedKey = selected.join(",")
    React.useEffect(() => {
        setLocal(Array.isArray(selected) ? selected.slice() : [])
    }, [selected, selectedKey])
    const hasActive = local.length > 0
    const toggle = (v: string) => {
        setLocal((prev) => (prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v]))
    }
    return (
        <div className="w-56 p-2 space-y-2">
            <div className="border rounded-md bg-muted/50 max-h-48 overflow-y-auto">
                {options.map((o) => (
                    <label key={o.value} className="flex items-center gap-2 cursor-pointer rounded px-2 py-1.5 hover:bg-muted/60">
                        <Checkbox checked={local.includes(o.value)} onCheckedChange={() => toggle(o.value)} />
                        <span className="text-sm">{o.label}</span>
                    </label>
                ))}
            </div>
            <div className="flex gap-2">
                <Button size="sm" variant="default" onClick={() => onApply(local.length ? local : null)}>Apply</Button>
                {hasActive && (
                    <Button size="sm" variant="ghost" className="text-muted-foreground" onClick={() => onApply(null)}>Clear</Button>
                )}
            </div>
        </div>
    )
}

function SingleDateFilterContent({
    value,
    onApply,
    placeholder = "Select date",
}: {
    value: string
    onApply: (v: string | null) => void
    placeholder?: string
}) {
    const parse = (s: string) => {
        if (!s || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return undefined
        const [y, m, d] = s.split("-").map(Number)
        return new Date(y, m - 1, d)
    }
    const date = parse(value)
    const [selected, setSelected] = React.useState<Date | undefined>(date)
    React.useEffect(() => {
        setSelected(date)
    }, [value, date])
    const hasActive = !!value
    const apply = () => {
        const str = selected ? format(selected, "yyyy-MM-dd") : null
        onApply(str)
    }
    return (
        <div className="w-auto p-2 space-y-2">
            <Calendar
                mode="single"
                selected={selected}
                onSelect={setSelected}
                defaultMonth={selected || new Date()}
            />
            <div className="flex gap-2">
                <Button size="sm" variant="default" onClick={apply}>Apply</Button>
                {hasActive && (
                    <Button size="sm" variant="ghost" className="text-muted-foreground" onClick={() => onApply(null)}>Clear</Button>
                )}
            </div>
        </div>
    )
}

function StatusFilterContent({ value, onApply }: { value: string; onApply: (v: string | null) => void }) {
    const urlVal = value === "true" ? "active" : value === "false" ? "inactive" : "all"
    const [local, setLocal] = React.useState(urlVal)
    React.useEffect(() => {
        setLocal(value === "true" ? "active" : value === "false" ? "inactive" : "all")
    }, [value])
    const hasActive = local !== "all"
    const apply = () => {
        const url = local === "all" ? null : local === "active" ? "true" : "false"
        onApply(url)
    }
    return (
        <div className="w-48 p-2 space-y-2">
            <div className="border rounded-md bg-muted/50">
                {statusFilterWithAllOptions.map((o) => (
                    <label key={o.value} className="flex items-center gap-2 cursor-pointer rounded px-2 py-1.5 hover:bg-muted/60">
                        <Checkbox checked={local === o.value} onCheckedChange={() => setLocal(o.value)} />
                        <span className="text-sm">{o.label}</span>
                    </label>
                ))}
            </div>
            <div className="flex gap-2">
                <Button size="sm" variant="default" onClick={apply}>Apply</Button>
                {hasActive && (
                    <Button size="sm" variant="ghost" className="text-muted-foreground" onClick={() => onApply(null)}>Clear</Button>
                )}
            </div>
        </div>
    )
}

function AddedFilterContent({
    addedFrom,
    addedTo,
    onApply,
}: {
    addedFrom: string
    addedTo: string
    onApply: (from: string | null, to: string | null) => void
}) {
    const parse = (s: string) => {
        if (!s || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return undefined
        const [y, m, d] = s.split("-").map(Number)
        return new Date(y, m - 1, d)
    }
    const fromDate = parse(addedFrom)
    const toDate = parse(addedTo)
    const [range, setRange] = React.useState<{ from?: Date; to?: Date } | undefined>(() =>
        fromDate || toDate ? { from: fromDate, to: toDate } : undefined
    )
    React.useEffect(() => {
        setRange(fromDate || toDate ? { from: fromDate, to: toDate } : undefined)
    }, [addedFrom, addedTo, fromDate, toDate])
    const hasActive = !!addedFrom || !!addedTo
    const apply = () => {
        const fromStr = range?.from ? format(range.from, "yyyy-MM-dd") : null
        const toStr = range?.to ? format(range.to, "yyyy-MM-dd") : (range?.from ? format(range.from, "yyyy-MM-dd") : null)
        onApply(fromStr, toStr)
    }
    return (
        <div className="w-auto p-2 space-y-2">
            <Calendar
                mode="range"
                selected={range === undefined ? undefined : { from: range.from, to: range.to }}
                onSelect={setRange}
                defaultMonth={range?.from || range?.to || new Date()}
                numberOfMonths={2}
            />
            <div className="flex gap-2">
                <Button size="sm" variant="default" onClick={apply}>Apply</Button>
                {hasActive && (
                    <Button size="sm" variant="ghost" className="text-muted-foreground" onClick={() => onApply(null, null)}>Clear</Button>
                )}
            </div>
        </div>
    )
}

type CompanyManagementTableProps = {
    data: TableDataItem[]
    isLoading: boolean
    /** Full list of organization names for the Organization filter dropdown (from API). When provided, used instead of current-page derived list. */
    organizationSuggestions?: string[]
    searchFilter?: string
    onSearchFilter?: (value: string | null) => void
    rolesFilter?: string
    onRolesFilter?: (roles: string[] | null) => void
    organizationFilter?: string
    onOrganizationFilter?: (value: string | null) => void
    arrivalDateFilter?: string
    onArrivalDateFilter?: (value: string | null) => void
    departureDateFilter?: string
    onDepartureDateFilter?: (value: string | null) => void
    addedFromFilter?: string
    addedToFilter?: string
    onAddedFilter?: (from: string | null, to: string | null) => void
    lastActiveAtFromFilter?: string
    lastActiveAtToFilter?: string
    onLastActiveFilter?: (from: string | null, to: string | null) => void
    statusFilter?: string
    onStatusFilter?: (value: string | null) => void
}

export default function VolunteersTable({
    data,
    isLoading,
    organizationSuggestions,
    searchFilter = "",
    onSearchFilter,
    rolesFilter = "",
    onRolesFilter,
    organizationFilter = "",
    onOrganizationFilter,
    addedFromFilter = "",
    addedToFilter = "",
    onAddedFilter,
    lastActiveAtFromFilter = "",
    lastActiveAtToFilter = "",
    onLastActiveFilter,
    statusFilter = "",
    onStatusFilter,
}: CompanyManagementTableProps) {
    const { regionSlug } = useRegion()
    const [sorting, setSorting] = React.useState<SortingState>([])
    const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([])
    const [columnVisibility, setColumnVisibility] =
        React.useState<VisibilityState>({})
    const [rowSelection, setRowSelection] = React.useState({})

    const rolesArray = React.useMemo(() => {
        if (!rolesFilter) return []
        return rolesFilter.split(",").map((s) => s.trim()).filter(Boolean)
    }, [rolesFilter])

    const userNames = React.useMemo(() => {
        const names = data.map((item) => getUserData(item).fullname).filter(Boolean) as string[]
        return [...new Set(names)].sort()
    }, [data])

    const organizationNamesFromData = React.useMemo(() => {
        const names = data.map((item) => getUserData(item).organizationName).filter((n): n is string => n != null && n !== "")
        return [...new Set(names)].sort()
    }, [data])

    const organizationNames = organizationSuggestions !== undefined
        ? organizationSuggestions
        : organizationNamesFromData

    const columns: ColumnDef<TableDataItem>[] = React.useMemo(() => [
        {
            id: "select",
            header: ({ table }) => (
                <Checkbox
                    checked={
                        table.getIsAllPageRowsSelected() ||
                        (table.getIsSomePageRowsSelected() && "indeterminate")
                    }
                    onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
                    aria-label="Select all"
                    className="rounded-md border-muted-foreground/70 data-[state=checked]:border-primary"
                />
            ),
            cell: ({ row }) => (
                <Checkbox
                    checked={row.getIsSelected()}
                    onCheckedChange={(value) => row.toggleSelected(!!value)}
                    aria-label="Select row"
                    className="rounded-md border-muted-foreground/70 data-[state=checked]:border-primary"
                />
            ),
            enableSorting: false,
            enableHiding: false,
        },
        {
            accessorKey: "user",
            header: () => {
                if (!onSearchFilter) return <InnerTh>Name</InnerTh>
                const hasActive = !!searchFilter
                return (
                    <InnerTh>
                        <div className="flex items-center gap-1.5">
                            <span>Name</span>
                            <Popover>
                                <PopoverTrigger asChild>
                                    <Button variant="ghost" size="icon" className="h-7 w-7 rounded" aria-label="Filter by Volunteer">
                                        <Filter className={cn("h-3.5 w-3.5", hasActive && "text-primary")} />
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent align="start" className="w-auto p-0">
                                    <ComboBoxFilterContent
                                        value={searchFilter}
                                        onApply={onSearchFilter}
                                        suggestions={userNames}
                                        placeholder="Type to search names…"
                                    />
                                </PopoverContent>
                            </Popover>
                        </div>
                    </InnerTh>
                )
            },
            cell: ({ row }) => {
                const user = getUserData(row.original)
                return (
                    <div className="text-left flex items-center gap-2">
                        <Avatar>
                            <AvatarImage src={user.profilePicture ?? undefined} className="object-cover object-top" />
                            <AvatarFallback className="bg-muted text-sm">{shortenName(user.fullname)}</AvatarFallback>
                        </Avatar>
                        <div className="">
                            <VolunteerNameCell user={user} item={row.original} />
                        </div>
                    </div>
                )
            },
        },
        {
            accessorKey: "organization",
            header: () => {
                if (!onOrganizationFilter) return <InnerTh>Organization</InnerTh>
                const hasActive = !!organizationFilter
                return (
                    <InnerTh>
                        <div className="flex items-center gap-1.5">
                            <span>Organization</span>
                            <Popover>
                                <PopoverTrigger asChild>
                                    <Button variant="ghost" size="icon" className="h-7 w-7 rounded" aria-label="Filter by Organization">
                                        <Filter className={cn("h-3.5 w-3.5", hasActive && "text-primary")} />
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent align="start" className="w-auto p-0">
                                    <ComboBoxFilterContent
                                        value={organizationFilter}
                                        onApply={onOrganizationFilter}
                                        suggestions={organizationNames}
                                        placeholder="Type to search organizations…"
                                    />
                                </PopoverContent>
                            </Popover>
                        </div>
                    </InnerTh>
                )
            },
            cell: ({ row }) => {
                const user = getUserData(row.original)
                const recentGroup = 'recentGroup' in row.original ? row.original.recentGroup : null
                if (!user.organizationName) return <div />
                if (!recentGroup) return <div>{user.organizationName}</div>
                const params = { from: 'groups', tab: 'members', returnToGroupId: String(recentGroup.id) }
                const href = regionSlug
                    ? buildRegionUrl(regionSlug, `/volunteers/groups/${recentGroup.id}`, params)
                    : `/volunteers/groups/${recentGroup.id}?${new URLSearchParams(params).toString()}`
                return (
                    <Link href={href} className="text-primary hover:underline">
                        {user.organizationName}
                    </Link>
                )
            },
        },
        {
            accessorKey: 'role',
            header: () => {
                if (!onRolesFilter) return <InnerTh>Role</InnerTh>
                const hasActive = rolesArray.length > 0
                return (
                    <InnerTh>
                        <div className="flex items-center gap-1.5">
                            <span>Role</span>
                            <Popover>
                                <PopoverTrigger asChild>
                                    <Button variant="ghost" size="icon" className="h-7 w-7 rounded" aria-label="Filter by Role">
                                        <Filter className={cn("h-3.5 w-3.5", hasActive && "text-primary")} />
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent align="start" className="w-auto p-0">
                                    <RolesFilterContent value={rolesArray} onApply={onRolesFilter} options={roleFilterOptions} />
                                </PopoverContent>
                            </Popover>
                        </div>
                    </InnerTh>
                )
            },
            cell: ({ row }) => {
                const user = getUserData(row.original)
                return (
                    <div className="font-medium">{user.role.name}</div>
                )
            },
        },
        {
            accessorKey: 'phoneNumber',
            header: () => <InnerTh>Phone Number</InnerTh>,
            cell: ({ row }) => {
                const user = getUserData(row.original)
                return (
                    <div>{formatPhoneNumber(user.phone ?? undefined)}</div>
                )
            },
        },
        {
            accessorKey: 'added',
            header: () => {
                if (!onAddedFilter) return <InnerTh>Added</InnerTh>
                const hasActive = !!addedFromFilter || !!addedToFilter
                return (
                    <InnerTh>
                        <div className="flex items-center gap-1.5">
                            <span>Added</span>
                            <Popover>
                                <PopoverTrigger asChild>
                                    <Button variant="ghost" size="icon" className="h-7 w-7 rounded" aria-label="Filter by Added date">
                                        <Filter className={cn("h-3.5 w-3.5", hasActive && "text-primary")} />
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent align="start" className="w-auto p-0">
                                    <AddedFilterContent addedFrom={addedFromFilter} addedTo={addedToFilter} onApply={onAddedFilter} />
                                </PopoverContent>
                            </Popover>
                        </div>
                    </InnerTh>
                )
            },
            cell: ({ row }) => {
                const user = getUserData(row.original)
                const u = user as { createdAt?: string; joinedAt?: string }
                const addedDate = parseDateValue(u.createdAt ?? u.joinedAt)
                return (
                    <div>{addedDate ? format(addedDate, "dd-MMM-yyyy") : "N/A"}</div>
                )
            },
        },
        {
            accessorKey: 'status',
            header: () => {
                if (!onStatusFilter) return <InnerTh>Status</InnerTh>
                const hasActive = !!statusFilter
                return (
                    <InnerTh>
                        <div className="flex items-center gap-1.5">
                            <span>Status</span>
                            <Popover>
                                <PopoverTrigger asChild>
                                    <Button variant="ghost" size="icon" className="h-7 w-7 rounded" aria-label="Filter by Status">
                                        <Filter className={cn("h-3.5 w-3.5", hasActive && "text-primary")} />
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent align="start" className="w-auto p-0">
                                    <StatusFilterContent value={statusFilter} onApply={onStatusFilter} />
                                </PopoverContent>
                            </Popover>
                        </div>
                    </InnerTh>
                )
            },
            cell: ({ row }) => {
                const volunteerData = getVolunteerData(row.original)
                const status = volunteerData.status
                if (status !== true) {
                    return (
                        <Badge variant={"outline"} className="border-foreground rounded-full flex items-center gap-2 justify-center w-fit">
                            <span className="w-1.5 h-1.5 aspect-square rounded-full bg-foreground"></span>
                            Inactive
                        </Badge>
                    )
                }
                return (
                    <Badge variant={"default"} className="bg-primary/20 text-foreground border-0 h-6 rounded-full flex items-center gap-2 justify-center w-fit">
                        <span className="w-1.5 h-1.5 aspect-square rounded-full bg-green-600"></span>
                        Active
                    </Badge>
                )
            },
        },
        {
            accessorKey: 'lastActiveAt',
            header: () => {
                if (!onLastActiveFilter) return <InnerTh>Last Active</InnerTh>
                const hasActive = !!lastActiveAtFromFilter || !!lastActiveAtToFilter
                return (
                    <InnerTh>
                        <div className="flex items-center gap-1.5">
                            <span>Last Active</span>
                            <Popover>
                                <PopoverTrigger asChild>
                                    <Button variant="ghost" size="icon" className="h-7 w-7 rounded" aria-label="Filter by Last Active date">
                                        <Filter className={cn("h-3.5 w-3.5", hasActive && "text-primary")} />
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent align="start" className="w-auto p-0">
                                    <AddedFilterContent addedFrom={lastActiveAtFromFilter} addedTo={lastActiveAtToFilter} onApply={onLastActiveFilter} />
                                </PopoverContent>
                            </Popover>
                        </div>
                    </InnerTh>
                )
            },
            cell: ({ row }) => {
                const user = getUserData(row.original)
                const lastActive = user.lastActiveAt ? parseDateValue(user.lastActiveAt) : null
                return (
                    <div>{lastActive ? format(lastActive, "dd-MMM-yyyy") : "N/A"}</div>
                )
            },
        },
        {
            id: "actions",
            header: () => <UserActionsHeader />,
            cell: ({ row }) => {
                return <UserActionsCell user={row.original} />
            },
        },
    ], [
        searchFilter,
        onSearchFilter,
        userNames,
        organizationNames,
        rolesArray,
        onRolesFilter,
        organizationFilter,
        onOrganizationFilter,
        addedFromFilter,
        addedToFilter,
        onAddedFilter,
        lastActiveAtFromFilter,
        lastActiveAtToFilter,
        onLastActiveFilter,
        statusFilter,
        onStatusFilter,
    ])

    const table = useReactTable({
        data,
        columns,
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        initialState: { pagination: { pageSize: 50 } },
        getFilteredRowModel: getFilteredRowModel(),
        onColumnVisibilityChange: setColumnVisibility,
        onRowSelectionChange: setRowSelection,
        state: {
            sorting,
            columnFilters,
            columnVisibility,
            rowSelection,
        },
        getRowId: originalRow => originalRow.id.toString(),
    })

    // This useEffect show delete button when rows are selected button
    // and hide when no rows are selected
    // useEffect(() => {
    //     setRows(rowSelection)
    // }, [rowSelection])

    return (
        <div className="w-full">
            <div className="rounded-md grid overflow-x-auto">
                <Table>
                    <TableHeader className="[&_tr]:border-b-0 bg-muted">
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => {
                                    return (
                                        <TableHead key={header.id} className="w-max">
                                            {header.isPlaceholder
                                                ? null
                                                : flexRender(
                                                    header.column.columnDef.header,
                                                    header.getContext()
                                                )}
                                        </TableHead>
                                    )
                                })}
                            </TableRow>
                        ))}
                    </TableHeader>
                    <VolunteerTableBody table={table} isLoading={isLoading} />
                </Table>
            </div>
        </div>
    )
}

const InnerTh = (props: PropsWithChildren & { className?: string }) => {
    return (
        <div className={cn("text-left w-max", props.className)}>{props.children}</div>
    )
}

// Component for volunteer name cell - only Admin/Base Leader get clickable link
function VolunteerNameCell({ user, item }: { user: ReturnType<typeof getUserData>; item: TableDataItem }) {
    const { session } = useSession()
    const canEditDelete = session?.role === UserRole.ADMIN || session?.role === UserRole.BASE_LEADER

    if (canEditDelete) {
        return (
            <UpdateUserLink user={item}>
                {user.fullname}
            </UpdateUserLink>
        )
    }
    return <span>{user.fullname}</span>
}

// Component for actions header that can use hooks
function UserActionsHeader() {
    const { session } = useSession()
    const canEditDelete = session?.role === UserRole.ADMIN || session?.role === UserRole.BASE_LEADER
    
    if (!canEditDelete) {
        return null
    }
    
    return <InnerTh className="ml-auto">Actions</InnerTh>
}

// Component for actions cell that can use hooks
function UserActionsCell({ user }: { user: TableDataItem }) {
    const { session } = useSession()
    const canEditDelete = session?.role === UserRole.ADMIN || session?.role === UserRole.BASE_LEADER
    
    if (!canEditDelete) {
        return null
    }
    
    return (
        <div className="flex justify-end items-center gap-2">
            <EditUserButton user={user} />
            <DeleteUserButton user={user} />
        </div>
    )
}

const TableRow = React.forwardRef<
    HTMLTableRowElement,
    React.HTMLAttributes<HTMLTableRowElement>
>(({ className, ...props }, ref) => (
    <DefaultTableRow
        ref={ref}
        className={cn(
            "border-b-0",
            className
        )}
        {...props}
    />
))
TableRow.displayName = "TableRow"
